import { env } from "../config/env";
import { createClient } from "./http";
import { getAuthToken } from "../pages/AdminAuth/components/authStore";

const catalogApi = createClient(env.CATALOG_BASE_URL);
const inventoryApi = createClient(env.WAREHOUSE_INVENTORY_BASE_URL);
const deliveryPartnerApi = createClient(env.DELIVERY_PARTNER_BASE_URL);

function authHeaders() {
  const token = getAuthToken?.() || "";
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function getWarehouseId() {
  return (
    localStorage.getItem("warehouseId") ||
    localStorage.getItem("selectedWarehouseId") ||
    ""
  );
}

function buildImageUrl(path) {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  return `${env.CATALOG_BASE_URL}/${path}`.replace(/([^:]\/)\/+/g, "$1");
}

function pickArray(value) {
  return Array.isArray(value) ? value : [];
}

function cleanParams(params = {}) {
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => {
      if (value === undefined || value === null) return false;
      if (typeof value === "string" && value.trim() === "") return false;
      return true;
    }),
  );
}

function normalizeVariant(item = {}, index = 0) {
  const name = item?.variantName || item?.variant_name || item?.name || item?.label || "";
  const unitValue = item?.unitValue || item?.unit_value || item?.weightValue || item?.weight_value || "";
  const unitSymbol = item?.unitSymbol || item?.unit_symbol || item?.weightUnitSymbol || item?.weight_unit_symbol || "";
  return {
    ...item,
    id: item?.id || item?._id || "",
    variantName: name,
    name: name || [unitValue, unitSymbol].filter(Boolean).join("") || `Variant ${index + 1}`,
    label: item?.label || name || [unitValue, unitSymbol].filter(Boolean).join(" "),
    unitValue,
    unitSymbol,
    weightValue: item?.weightValue || item?.weight_value || "",
    weightUnitSymbol: item?.weightUnitSymbol || item?.weight_unit_symbol || "",
    isDefault: Boolean(item?.isDefault ?? item?.is_default ?? index === 0),
    mrp: item?.mrp ?? null,
    sellingPrice: item?.sellingPrice ?? item?.selling_price ?? null,
  };
}

async function downloadFile(path, fallbackName = "download.xlsx") {
  const base = env.WAREHOUSE_INVENTORY_BASE_URL;
  const token = getAuthToken?.() || "";

  const res = await fetch(`${base}${path}`, {
    method: "GET",
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  if (!res.ok) {
    let message = "File download failed";
    try {
      const data = await res.json();
      message = data?.message || message;
    } catch {
      // ignore json parse errors
    }
    throw new Error(message);
  }

  const blob = await res.blob();
  const cd = res.headers.get("content-disposition") || "";
  const match = cd.match(/filename="?([^"]+)"?/i);
  const fileName = match?.[1] || fallbackName;

  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);

  return true;
}

export const warehouseInventoryService = {
  async listMasterProducts({
    search = "",
    page = 1,
    limit = 20,
    warehouseId = "",
  } = {}) {
    const finalWarehouseId = warehouseId || getWarehouseId();
    const offset = Math.max(0, (page - 1) * limit);

    const res = await catalogApi.get("/api/admin/products", {
      params: {
        limit,
        offset,
        warehouseId: finalWarehouseId,
      },
      headers: authHeaders(),
    });

    const raw = Array.isArray(res)
      ? res
      : Array.isArray(res?.items)
        ? res.items
        : Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res?.products)
            ? res.products
            : [];

    const total =
      res?.total ??
      res?.count ??
      res?.pagination?.total ??
      raw.length ??
      0;

    let filtered = raw;

    if (search?.trim()) {
      const q = search.trim().toLowerCase();
      filtered = raw.filter((item) => {
        const name = item?.name?.toLowerCase?.() || "";
        const sku = item?.sku?.toLowerCase?.() || "";
        const brand = item?.brandName?.toLowerCase?.() || item?.brand_name?.toLowerCase?.() || "";
        const category = item?.categoryName?.toLowerCase?.() || item?.category_name?.toLowerCase?.() || "";
        const variantText = pickArray(item?.variants).map((v) => `${v?.variantName || v?.name || ""} ${v?.weightValue || ""} ${v?.weightUnitSymbol || ""}`.toLowerCase()).join(" ");
        return name.includes(q) || sku.includes(q) || brand.includes(q) || category.includes(q) || variantText.includes(q);
      });
    }

    return {
      items: filtered.map((item) => {
        const variants = (pickArray(item?.variants).length ? pickArray(item?.variants) : [{
          id: item?.defaultVariantId || "",
          variantName: item?.variantName || [item?.weightValue, item?.weightUnitSymbol].filter(Boolean).join("") || "Default",
          weightValue: item?.weightValue || "",
          weightUnitSymbol: item?.weightUnitSymbol || "",
          isDefault: true,
          mrp: item?.mrp || "",
          sellingPrice: item?.sellingPrice || "",
        }]).map(normalizeVariant);
        const defaultVariant = variants.find((x) => x?.isDefault) || variants[0] || null;
        return ({
          id: item?._id || item?.id,
          name: item?.name || "",
          sku: item?.sku || "",
          image_url: buildImageUrl(item?.imageUrl),
          category_name: item?.categoryName || "",
          brand_name: item?.brandName || "",
          unit_symbol: item?.unitSymbol || "",
          unit_name: item?.unitName || "",
          mrp: item?.mrp || "",
          selling_price: item?.sellingPrice || "",
          barcode: item?.barcode || "",
          short_description: item?.shortDescription || "",
          description: item?.description || "",
          tax_percent: item?.taxPercent || "",
          weight_value: item?.weightValue || "",
          weight_unit_symbol: item?.weightUnitSymbol || "",
          is_active: item?.isActive,
          status: item?.status || "",
          variants,
          defaultVariant,
          defaultVariantId: defaultVariant?.id || "",
        });
      }),
      total: search?.trim() ? filtered.length : Number(total || 0),
      page,
      limit,
    };
  },

  async createOrder(payload) {
    return inventoryApi.post("/api/warehouse/orders", payload, {
      headers: authHeaders(),
    });
  },

  async listOrders({ status = "", page = 1, limit = 20 } = {}) {
    return inventoryApi.get("/api/warehouse/orders", {
      params: { status, page, limit },
      headers: authHeaders(),
    });
  },

  async getOrder(id) {
    if (!id) throw new Error("Order id is required");
    return inventoryApi.get(`/api/warehouse/orders/${id}`, {
      headers: authHeaders(),
    });
  },

  async exportOrder(id) {
    if (!id) throw new Error("Order id is required");
    return downloadFile(
      `/api/warehouse/orders/${id}/export`,
      `warehouse-order-${id}.xlsx`
    );
  },

  async listInboundReceipts({ status = "", page = 1, limit = 20 } = {}) {
    return inventoryApi.get("/api/warehouse/inbound-receipts", {
      params: { status, page, limit },
      headers: authHeaders(),
    });
  },

  async getInboundReceipt(id) {
    if (!id) throw new Error("Receipt id is required");
    return inventoryApi.get(`/api/warehouse/inbound-receipts/${id}`, {
      headers: authHeaders(),
    });
  },

  async receiveInboundReceipt(id, payload) {
    if (!id) throw new Error("Receipt id is required");
    return inventoryApi.post(`/api/warehouse/inbound-receipts/${id}/receive`, payload, {
      headers: authHeaders(),
    });
  },

  async printInboundReceipt(id, receiptCode) {
    if (!id) throw new Error("Receipt id is required");

    return downloadFile(
      `/api/warehouse/inbound-receipts/${id}/print`,
      `GRN-${receiptCode || id}.xlsx`
    );
  },

  async getStock({
    q = "",
    includeBatches = false,
    onlyLow = false,
    lowThreshold = "",
    expiringDays = "",
    categoryId = "",
    subCategoryId = "",
    subcategoryId = "",
    brandId = "",
  } = {}) {
    const finalSubcategoryId = subCategoryId || subcategoryId || "";

    return inventoryApi.get("/api/warehouse/stock", {
      params: cleanParams({
        q,
        includeBatches,
        onlyLow,
        lowThreshold,
        expiringDays,
        categoryId,
        subCategoryId: finalSubcategoryId,
        subcategoryId: finalSubcategoryId,
        brandId,
      }),
      headers: authHeaders(),
    });
  },

  async updateStockDisplayStatus({ productId, productVariantId, status }) {
    if (!productId) throw new Error("Product id is required");
    if (!productVariantId) throw new Error("Product variant id is required");

    return inventoryApi.patch(
      "/api/warehouse/stock/display-status",
      { productId, productVariantId, status },
      { headers: authHeaders() }
    );
  },

  async markStockZero({ productId, productVariantId, reason, adjustmentType = "SPOILED" }) {
    if (!productId) throw new Error("Product id is required");
    if (!productVariantId) throw new Error("Product variant id is required");
    if (!String(reason || "").trim()) throw new Error("Reason is required");

    return inventoryApi.post(
      "/api/warehouse/stock/zero",
      { productId, productVariantId, reason: String(reason).trim(), adjustmentType },
      { headers: authHeaders() }
    );
  },


  async getExpiredStock({
    q = "",
    productId = "",
    limit = 100,
    offset = 0,
  } = {}) {
    return catalogApi.get("/api/warehouse/expired-stock", {
      params: {
        q,
        productId,
        limit,
        offset,
      },
      headers: authHeaders(),
    });
  },

  async getStockAlerts({ type = "", limit = 20 } = {}) {
    return catalogApi.get("/api/warehouse/stock/alerts", {
      params: { type, limit },
      headers: authHeaders(),
    });
  },


  async listCustomerOrders({
    status = "",
    paymentStatus = "",
    paymentMethod = "",
    fromDate = "",
    toDate = "",
    q = "",
    page = 1,
    limit = 20,
  } = {}) {
    return inventoryApi.get("/api/warehouse/customer-orders", {
      params: cleanParams({
        status,
        paymentStatus,
        paymentMethod,
        fromDate,
        toDate,
        q,
        page,
        limit,
      }),
      headers: authHeaders(),
    });
  },

  async getCustomerOrder(id) {
    if (!id) throw new Error("Customer order id is required");
    return inventoryApi.get(`/api/warehouse/customer-orders/${id}`, {
      headers: authHeaders(),
    });
  },

// map api

  async getWarehouseCustomerOrderTracking(id) {
    if (!id) throw new Error("Customer order id is required");
    return deliveryPartnerApi.get(`/api/warehouse/delivery-tracking/orders/${id}`, {
      headers: authHeaders(),
    });
  },

  async updateCustomerOrderStatus(id, payload) {
    if (!id) throw new Error("Customer order id is required");
    return inventoryApi.patch(
      `/api/warehouse/customer-orders/${id}/status`,
      payload,
      {
        headers: authHeaders(),
      }
    );
  },


  async cancelCustomerOrder(id, payload = {}) {
    if (!id) throw new Error("Customer order id is required");
    return inventoryApi.patch(
      `/api/warehouse/customer-orders/${id}/status`,
      { ...payload, status: "CANCELLED" },
      {
        headers: authHeaders(),
      }
    );
  },

  async getCustomerOrderManualAssignmentOptions(id, { includeUnavailable = false } = {}) {
    if (!id) throw new Error("Customer order id is required");
    return inventoryApi.get(`/api/warehouse/customer-orders/${id}/manual-assignment-options`, {
      params: { includeUnavailable },
      headers: authHeaders(),
    });
  },

  async manualAssignCustomerOrder(id, payload) {
    if (!id) throw new Error("Customer order id is required");
    return inventoryApi.post(
      `/api/warehouse/customer-orders/${id}/manual-assign`,
      payload,
      {
        headers: authHeaders(),
      }
    );
  },


  //Analytics api

  async getOrderAnalyticsSummary({
    paymentStatus = "PAID",
    fromDate = "",
    toDate = "",
    status = "",
  } = {}) {
    return inventoryApi.get("/api/warehouse/order-analytics/summary", {
      params: { paymentStatus, fromDate, toDate, status },
      headers: authHeaders(),
    });
  },

  async getOrderAnalyticsSalesTrend({
    paymentStatus = "PAID",
    fromDate = "",
    toDate = "",
    status = "",
    period = "month",
  } = {}) {
    return inventoryApi.get("/api/warehouse/order-analytics/sales-trend", {
      params: { paymentStatus, fromDate, toDate, status, period },
      headers: authHeaders(),
    });
  },

  async getOrderAnalyticsProducts({
    paymentStatus = "PAID",
    fromDate = "",
    toDate = "",
    status = "",
    type = "top",
    limit = 5,
  } = {}) {
    return inventoryApi.get("/api/warehouse/order-analytics/products", {
      params: { paymentStatus, fromDate, toDate, status, type, limit },
      headers: authHeaders(),
    });
  },

  async getOrderAnalyticsOrders({
    paymentStatus = "PAID",
    fromDate = "",
    toDate = "",
    status = "",
    q = "",
    page = 1,
    limit = 10,
  } = {}) {
    return inventoryApi.get("/api/warehouse/order-analytics/orders", {
      params: { paymentStatus, fromDate, toDate, status, q, page, limit },
      headers: authHeaders(),
    });
  },

  async getOrderAnalyticsDashboard(params = {}) {
    return inventoryApi.get("/api/warehouse/order-analytics/dashboard", {
      params: cleanParams(params),
      headers: authHeaders(),
    });
  },

  async getSalesAnalyticsSummary(params = {}) {
    return inventoryApi.get("/api/warehouse/order-analytics/sales-summary", {
      params: cleanParams(params),
      headers: authHeaders(),
    });
  },

  async getSalesAnalyticsCategories(params = {}) {
    return inventoryApi.get("/api/warehouse/order-analytics/categories", {
      params: cleanParams(params),
      headers: authHeaders(),
    });
  },

  async getSalesAnalyticsSubcategories(params = {}) {
    return inventoryApi.get("/api/warehouse/order-analytics/subcategories", {
      params: cleanParams(params),
      headers: authHeaders(),
    });
  },

  async getSalesAnalyticsProducts(params = {}) {
    return inventoryApi.get("/api/warehouse/order-analytics/products", {
      params: cleanParams(params),
      headers: authHeaders(),
    });
  },

  async getSalesAnalyticsSales(params = {}) {
    return inventoryApi.get("/api/warehouse/order-analytics/sales", {
      params: cleanParams(params),
      headers: authHeaders(),
    });
  },

  async getSalesAnalyticsProductTrend(params = {}) {
    return inventoryApi.get("/api/warehouse/order-analytics/product-trend", {
      params: cleanParams(params),
      headers: authHeaders(),
    });
  },

 // broadcast api
async getActiveWarehouseBroadcast() {
  return inventoryApi.get("/api/warehouse/broadcasts/active", {
    headers: authHeaders(),
  });
},

async markWarehouseBroadcastViewed(id, payload = {}) {
  return inventoryApi.post(`/api/warehouse/broadcasts/${id}/view`, payload, {
    headers: authHeaders(),
  });
},

};

